import { useState, useCallback } from 'react';
import insightsService from '../../../services/insights.service';
import { debugError } from '../../../utils/debugLog';

/**
 * useOperationalInsights Hook
 * Manages operational insights state and loading
 */
export const useOperationalInsights = (isAuthenticated, authLoading) => {
  const [operationalBatches, setOperationalBatches] = useState([]);
  const [operationalBatchInsights, setOperationalBatchInsights] =
    useState(null);
  const [operationalLoading, setOperationalLoading] = useState(false);
  const [selectedOperationalBatchId, setSelectedOperationalBatchId] =
    useState(null);
  const [error, setError] = useState(null);

  const loadOperationalBatchInsights = useCallback(
    async batchId => {
      if (!isAuthenticated || authLoading || !batchId) {
        return;
      }
      try {
        setOperationalLoading(true);
        setError(null);
        const response = await insightsService.getBatchInsights(batchId);
        setOperationalBatchInsights(response);
      } catch (err) {
        debugError('Failed to load operational batch insights:', err);
        setError(err.message || 'Failed to load operational batch insights');
      } finally {
        setOperationalLoading(false);
      }
    },
    [isAuthenticated, authLoading]
  );

  const loadLatestOperationalBatches = useCallback(async () => {
    if (!isAuthenticated || authLoading) return;

    try {
      setOperationalLoading(true);
      setError(null);

      const response = await insightsService.listBatches({ limit: 10 });

      if (response && response.batches && response.batches.length > 0) {
        const operationalBatchIds = response.batches.filter(batch => {
          const insightsByType = batch.insights_by_type || {};
          return (
            !insightsByType.template_version_comparison ||
            insightsByType.template_version_comparison === 0
          );
        });

        if (operationalBatchIds.length > 0) {
          setOperationalBatches(operationalBatchIds);
          const latestOperational = operationalBatchIds[0];
          setSelectedOperationalBatchId(latestOperational.batch_id);
          await loadOperationalBatchInsights(latestOperational.batch_id);
        }
      }
    } catch (err) {
      debugError('Failed to load operational batches:', err);
      setError(err.message || 'Failed to load operational batches');
    } finally {
      setOperationalLoading(false);
    }
  }, [isAuthenticated, authLoading, loadOperationalBatchInsights]);

  return {
    operationalBatches,
    operationalBatchInsights,
    operationalLoading,
    selectedOperationalBatchId,
    error,
    setOperationalBatches,
    setSelectedOperationalBatchId,
    loadOperationalBatchInsights,
    loadLatestOperationalBatches,
  };
};
